import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import PortalLayout from './components/layout/PortalLayout';
import LandingPage from './pages/LandingPage';
import Home from './pages/Home';
import SearchPage from './pages/SearchPage';
import CodeDetailPage from './pages/CodeDetailPage';
import AIToolsPage from './pages/AIToolsPage';
import AboutPage from './pages/AboutPage';
import CodeTypeListPage from './pages/CodeTypeListPage';
import HistoryPage from './pages/HistoryPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ProfilePage from './pages/settings/ProfilePage';
import SecurityPage from './pages/settings/SecurityPage';
import SubscriptionPage from './pages/settings/SubscriptionPage';
import BillingPage from './pages/settings/BillingPage';
import AccountPage from './pages/settings/AccountPage';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '631872581040-jnr7hp2ai1p5kdeu90rjbfsqmrnjs9g4.apps.googleusercontent.com';

const queryClient = new QueryClient({
    defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } },
});

// ✅ Home route — guest sees LandingPage, logged in sees Dashboard
const HomeRoute = () => {
    const { isLoggedIn } = useAuth();
    return isLoggedIn ? <Home /> : <LandingPage />;
};

function App() {
    return (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <Router>
                        <Routes>
                            {/* Auth pages — no layout */}
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/register" element={<RegisterPage />} />

                            {/* All pages — PortalLayout handles guest vs logged-in */}
                            <Route element={<PortalLayout />}>
                                <Route path="/" element={<HomeRoute />} />
                                <Route path="/search" element={<SearchPage />} />
                                <Route path="/codes/:codeType" element={<CodeTypeListPage />} />
                                <Route path="/code/:codeType/:code" element={<CodeDetailPage />} />
                                <Route path="/ai-tools" element={<AIToolsPage />} />
                                <Route path="/about" element={<AboutPage />} />
                                <Route path="/history" element={<HistoryPage />} />
                                <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
                                <Route path="/settings/profile" element={<ProfilePage />} />
                                <Route path="/settings/security" element={<SecurityPage />} />
                                <Route path="/settings/subscription" element={<SubscriptionPage />} />
                                <Route path="/settings/billing" element={<BillingPage />} />
                                <Route path="/settings/account" element={<AccountPage />} />
                                <Route path="*" element={<Navigate to="/" replace />} />
                            </Route>
                        </Routes>

                        <Toaster
                            position="top-right"
                            toastOptions={{
                                duration: 3000,
                                style: { borderRadius: '12px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans, sans-serif' },
                                success: { iconTheme: { primary: '#0d9488', secondary: '#fff' } },
                                error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
                            }}
                        />
                    </Router>
                </AuthProvider>
            </QueryClientProvider>
        </GoogleOAuthProvider>
    );
}

export default App;